import type { CSSProperties } from 'react';
import { ROLE_LABELS, type LineupSlot, type LineupSlotOccupant } from '../../services/LineupService';
import { LineupPitchSlot } from './LineupPitchSlot';
import { buildLineupRows } from './lineupEditorUtils';

interface LineupPitchProps {
  lineup: LineupSlot<LineupSlotOccupant>[];
  tactics: string;
  onSlotClick?: (position: number) => void;
  mode?: 'edit' | 'view';
}

export const LineupPitch = ({
  lineup,
  tactics,
  onSlotClick,
  mode = 'edit',
}: LineupPitchProps) => {
  const handleSlotClick = onSlotClick ?? (() => {});
  const rows = buildLineupRows(lineup, tactics);

  const getRowJustification = (slotsCount: number): CSSProperties['justifyContent'] => {
    if (slotsCount <= 1) {
      return 'center';
    }

    if (slotsCount === 2) {
      return 'space-around';
    }

    return 'space-between';
  };

  return (
    <div
      className="relative overflow-hidden rounded-[2rem] border border-emerald-300/80 bg-gradient-to-b from-emerald-500 via-emerald-600 to-emerald-700 px-3 py-5 shadow-[0_24px_60px_-28px_rgba(5,46,22,0.85)] sm:px-5 sm:py-6"
      data-testid="lineup-pitch"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.12)_0,rgba(255,255,255,0.06)_35%,transparent_70%)]" />
      <div className="pointer-events-none absolute inset-3 rounded-[1.65rem] border-2 border-white/35 sm:inset-4" />
      <div className="pointer-events-none absolute inset-x-3 top-1/2 h-px -translate-y-1/2 bg-white/35 sm:inset-x-4" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/30 sm:h-24 sm:w-24" />
      <div className="pointer-events-none absolute left-1/2 top-4 h-12 w-28 -translate-x-1/2 rounded-b-[1.5rem] border-x-2 border-b-2 border-white/30 sm:top-5 sm:h-14 sm:w-36" />
      <div className="pointer-events-none absolute bottom-4 left-1/2 h-12 w-28 -translate-x-1/2 rounded-t-[1.5rem] border-x-2 border-t-2 border-white/30 sm:bottom-5 sm:h-14 sm:w-36" />

      <div className="relative flex min-h-[520px] flex-col justify-between gap-4 sm:min-h-[600px]">
        {rows.map((row) => (
          <div key={row.role} className="flex justify-center" data-testid={`pitch-row-${row.role}`}>
            <div
              className="flex w-full items-start"
              style={{
                justifyContent: getRowJustification(row.slots.length),
              }}
            >
              {row.slots.map((slot) => (
                <div
                  key={slot.position}
                  style={{ width: 'calc((100% - 2rem) / 5)' }}
                >
                  <LineupPitchSlot
                    slot={slot}
                    requiredRole={ROLE_LABELS[row.role]}
                    onSelect={() => handleSlotClick(slot.position)}
                    mode={mode}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
